// src/pages/outsourcing_management/InquiryConvertForm.tsx
//
// Turning an inquiry into a purchase order, in the inquiry dialog itself - the
// same way editing the inquiry is an in-place form, not a second dialog.
//
// It serves both halves of placing an order, by `mode`:
//   "convert" - what we are about to send the supplier. The supplier is still
//               being chosen, the date and method are required, and the part
//               files can be left off the email.
//   "confirm" - what the supplier came back with, days later. Same figures,
//               but the supplier is settled and shown rather than picked (a
//               different supplier is a different order), nothing is required
//               because an order confirmed as sent is confirmed by pressing
//               the button, no files are listed because no email goes out,
//               and the lead time they quoted is editable too.
//
// Only the fields the conversion actually decides are editable here: which
// supplier won the work, the date the parts are due, what each line costs
// (EE-253), and the note printed on the order. Everything else the inquiry says is shown with the normal read
// components rather than as disabled inputs, and the item lines are the
// read-only table - the order carries the same lines as the inquiry, so there
// is nothing to change on them.
//
// The values are reported up (onValuesChange) so the column beside the form
// can preview the purchase order as it is being filled; the Confirm action
// lives in the dialog's actions column, not here.
import {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import {
  apiGetInquiryAttachments,
  InquiryAttachmentType,
} from "util/network/Inquiries";
import { InquiryDetailType } from "types/InquiryTypes";
import { SupplierAutocomplete } from "components/Dashboard/supplierOption";
import { toDateInput, DELIVERY_METHODS } from "./inquiryFormValues";
import InquiryItemsTable from "components/Inquiry/items/InquiryItemsTable";
import { lineFromInquiryItem } from "components/Inquiry/items/inquiryLine";
import { ItemLinesScroll } from "./InquiryDialogLayout";
import { DetailsDisclosure } from "./InquiryDetailsDisclosure";
import InquiryPriceFields, {
  PriceValues,
  usePriceDraft,
} from "./InquiryPriceFields";

export type ConvertValues = Partial<PriceValues> & {
  partnerId?: number;
  deadline: string;
  deliveryMethod: string;
  deliveryTime?: string;
  message: string;
  leftOut: string[];
};

type PropsType = {
  inquiry: InquiryDetailType;
  /** Reports what the form holds, so the preview beside it stays in step. */
  onValuesChange: (values: ConvertValues) => void;
  /** Which half of the order this is - see the file header. */
  mode?: "convert" | "confirm";
};

const InquiryConvertForm: FunctionComponent<PropsType> = ({
  inquiry,
  onValuesChange,
  mode = "convert",
}) => {
  const confirming = mode === "confirm";
  const { token } = useContext(AppStateContext);
  const orderCandidates = inquiry.partnerCandidates ?? [];
  // A partner set before the shortlist existed is not offered: the server
  // refuses a winner the inquiry was not sent to. One name on the shortlist
  // is not a choice, it is the answer.
  const settled =
    !!inquiry.partnerId &&
    (orderCandidates.length === 0 ||
      orderCandidates.some((c) => c.partner_id === inquiry.partnerId));
  const [partnerId, setPartnerId] = useState<number | undefined>(
    settled
      ? inquiry.partnerId ?? undefined
      : orderCandidates.length === 1
      ? orderCandidates[0].partner_id
      : undefined
  );
  const [deadline, setDeadline] = useState(toDateInput(inquiry.deliveryDate));
  const [deliveryMethod, setDeliveryMethod] = useState(
    inquiry.deliveryMethod ?? ""
  );
  const prices = usePriceDraft(inquiry);
  const [deliveryTime, setDeliveryTime] = useState(inquiry.deliveryTime ?? "");
  const [message, setMessage] = useState(inquiry.message ?? "");
  const [attachments, setAttachments] = useState<
    InquiryAttachmentType[] | null
  >(null);
  const [leftOut, setLeftOut] = useState<string[]>([]);

  useEffect(() => {
    // Nothing is emailed by a confirmation, so there is no leave-out list to
    // fetch - and asking for one would suggest otherwise.
    if (confirming) {
      setAttachments([]);
      return;
    }
    apiGetInquiryAttachments(token, inquiry.id)
      .then(setAttachments)
      // Without the list the order still goes out; it just cannot leave
      // anything off. Show it as empty rather than block.
      .catch(() => setAttachments([]));
  }, [token, inquiry.id, confirming]);

  useEffect(() => {
    onValuesChange({
      partnerId,
      deadline,
      deliveryMethod,
      ...prices.values,
      deliveryTime,
      message,
      leftOut,
    });
    // onValuesChange is a setState from the dialog above; listing it would
    // re-run this on every render of that dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    partnerId,
    deadline,
    deliveryMethod,
    prices.values,
    deliveryTime,
    message,
    leftOut,
  ]);

  const partName = (itemId: number) =>
    inquiry.items.find((item) => item.id === itemId)?.product?.name;

  return (
    <div style={{ marginBottom: "1rem" }}>
      <Grid container spacing={2}>
        {confirming ? (
          // Settled when the order went out. Shown, not picked: the supplier
          // holding our purchase order is the only one who can confirm it.
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Ordered from
            </Typography>
            <Typography variant="body2">
              {inquiry.partner?.info?.company ??
                (inquiry.partnerId
                  ? `Partner #${inquiry.partnerId}`
                  : "No supplier")}
            </Typography>
          </Grid>
        ) : orderCandidates.length > 0 ? (
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              label="Ordered from"
              value={partnerId ?? ""}
              onChange={(e) =>
                setPartnerId(
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
            >
              {orderCandidates.map((candidate) => (
                <MenuItem key={candidate.id} value={candidate.partner_id}>
                  {candidate.partner_name ?? `Partner #${candidate.partner_id}`}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        ) : (
          <Grid item xs={12} sm={6}>
            <SupplierAutocomplete
              value={partnerId}
              onChange={setPartnerId}
              serviceType={inquiry.type}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label={confirming ? "Confirmed delivery date" : "Planned delivery date"}
            required={!confirming}
            InputLabelProps={{ shrink: true }}
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            helperText={
              confirming
                ? "The date the supplier confirmed - leave as sent if it did not move."
                : deadline
                ? undefined
                : "Required - the date the supplier is held to."
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            select
            label="Delivery method"
            required={!confirming}
            value={deliveryMethod}
            onChange={(e) => setDeliveryMethod(e.target.value)}
            helperText={
              confirming
                ? "How the parts get here, as confirmed."
                : deliveryMethod
                ? undefined
                : "Required - how the parts get here."
            }
          >
            {DELIVERY_METHODS.map((method) => (
              <MenuItem key={method} value={method}>
                {method}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* The lead time is the supplier's to state, so it is only editable
            where they have stated it. */}
        {confirming && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Delivery time"
              value={deliveryTime}
              onChange={(e) => setDeliveryTime(e.target.value)}
              helperText="Optional - the lead time the supplier confirmed."
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Note to the supplier"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            helperText={
              confirming
                ? "Kept with the order - a re-sent purchase order prints it."
                : "Printed on the purchase order, under the header."
            }
          />
        </Grid>

        {/* Whatever the inquiry already says is prefilled and can be
            corrected here before it goes out on paper. Empty stays empty: a
            price may simply not be agreed yet. */}
        {inquiry.items.length > 0 && (
          <Grid item xs={12}>
            <InquiryPriceFields inquiry={inquiry} draft={prices} />
          </Grid>
        )}

        {/* Leave part files off this order's email - the same checklist the
            send confirmation uses, so the leave-out is per order, not
            permanent. */}
        {!confirming && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Files attached
          </Typography>
          {attachments === null ? (
            <CircularProgress size={16} sx={{ mt: 1 }} />
          ) : attachments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          ) : (
            <FormGroup>
              {attachments.map((file) => (
                <FormControlLabel
                  key={file.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={!leftOut.includes(file.key)}
                      onChange={(e) =>
                        setLeftOut((current) =>
                          e.target.checked
                            ? current.filter((key) => key !== file.key)
                            : [...current, file.key]
                        )
                      }
                    />
                  }
                  label={
                    <span>
                      {file.name}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {file.type}
                        {partName(file.itemId) && ` · ${partName(file.itemId)}`}
                      </Typography>
                    </span>
                  }
                />
              ))}
            </FormGroup>
          )}
        </Grid>
        )}
      </Grid>

      {/* The rest of the inquiry, as the read view shows it - a buyer should
          be able to confirm everything the order will carry before it goes
          out, not just the three fields this form decides. Open, no toggle:
          the facts are part of the confirmation here. On a confirmation the
          price table closes the form and its last row's rule already draws
          the line - a divider on top of it read as two. */}
      {confirming && inquiry.items.length > 0 ? (
        <Box sx={{ mb: 2 }} />
      ) : (
        <Divider sx={{ my: 2 }} />
      )}
      <DetailsDisclosure inquiry={inquiry} />

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Item lines
      </Typography>
      {inquiry.items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No lines on this inquiry.
        </Typography>
      ) : (
        <ItemLinesScroll>
          <InquiryItemsTable
            mode="read"
            lines={inquiry.items.map(lineFromInquiryItem)}
            inquiryType={inquiry.type}
          />
        </ItemLinesScroll>
      )}
    </div>
  );
};

export default InquiryConvertForm;
